import { ReactNode } from "react";
import { isRunningInTest } from "../utils/env";
import { useAppSelector } from "../store";

export type TtgRenderParams = {
  treeIcon: ReactNode;
};

type TtgFeatureProps = {
  children: (params: TtgRenderParams) => ReactNode;
  renderElse?: ReactNode;
};

/**
 * For hiding stuff unless the TTG flag is TRUE for the user.
 * A render prop, passing a treeIcon you can use to indicate that the feature will only be visible
 * to TTG users.
 */
export default function TtgFeature({
  children,
  renderElse = null,
}: TtgFeatureProps) {
  return useRenderIfTtgUser(children) ?? renderElse;
}

type RenderTtgFeatureConditionalOptions = {
  key?: string;
  renderElse?: ReactNode;
};

/**
 * Render conditionally as a TtgFeature if the condition is true, otherwise
 * render for everyone.
 */
export function renderTtgFeatureConditional(
  ttgOnly: boolean,
  renderFunction: (params: TtgRenderParams) => ReactNode,
  options?: RenderTtgFeatureConditionalOptions
): ReactNode {
  options ??= {};
  return ttgOnly ? (
    <TtgFeature
      key={options.key}
      children={renderFunction}
      renderElse={options.renderElse}
    />
  ) : (
    renderFunction({ treeIcon: null })
  );
}

type TreeIconProps = {
  title?: string;
};

export function TreeIcon({
  title = "This feature is visible to TTG users only",
}: TreeIconProps) {
  return (
    <span
      role="img"
      aria-label="Tree"
      title={title}
      style={{ fontSize: 12, position: "relative", top: -1 }}
    >
      🌲
    </span>
  );
}

/**
 * Checks if the user is a tree user and if so, returns the tree icon.
 *
 * You must then display this tree icon to make it clear that the feature is
 * for tree users only.
 */
export function useTreeOnly(): ReactNode {
  return useShouldShowTtgFeature() ? <TreeIcon /> : null;
}

/**
 * Same as useTreeOnly(), but for the comments feature specifically. This is
 * in order to make it easier to find all instances of this usage for when we
 * remove the tree restriction for it in the future.
 */
export function useCommentsTreeOnly(): ReactNode {
  return useTreeOnly();
}

/**
 * Renders what the passed callback returns if the current user is a TTG user.
 * You can use this function directly instead of the TtgFeature component if it's important for
 * the children to not end up wrapped in another component (TtgFeature), e.g. for use in <Switch>
 * which is sensitive to which exact components get rendered as its direct children.
 */
export function useRenderIfTtgUser(
  toRenderCallback: (params: TtgRenderParams) => ReactNode
): ReactNode {
  if (!useShouldShowTtgFeature()) {
    return null;
  }

  return toRenderCallback({
    treeIcon: <TreeIcon />,
  });
}

export function useShouldShowTtgFeature(): boolean {
  const isTtgUser =
    // Treat all users as TTG users in tests.
    isRunningInTest ||
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAppSelector((state) => state.auth.ttg);

  return isTtgUser || process.env.NODE_ENV === "development";
}
